import { BrowserModule, Meta } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LinkModule } from '@vg-constellation/angular-19/link';
import { environment } from '@environments/environment';
import { WindowRef } from './services/window-ref/window-ref.service';
import { firstValueFrom } from 'rxjs';
import { IconModule } from '@vg-constellation/angular-19/icon';
import { NavOverflowModule } from '@vg-constellation/angular-19/nav-overflow';
import { SpinnerModule } from '@vg-constellation/angular-19/spinner';
import { StepIndicatorModule } from '@vg-constellation/angular-19/step-indicator';
import { CaasService } from './services/caas/caas.service';
import { ThirdpartyDocumentsComponent } from './components/thirdparty-documents/thirdparty-documents.component';
import { GatekeeperService } from './services/gatekeeper/gatekeeper.service';
import { ButtonModule } from '@vg-constellation/angular-19/button';
import { OptimizeYourAccountComponent } from './components/optimize-your-account/optimize-your-account.component';
import { CardComponent } from '@vg-constellation/angular-19/card';
import { NgOptimizedImage } from '@angular/common';
import { AuthInterceptor } from './auth.interceptor';
import { BannerComponent } from '@vg-constellation/angular-19/banner';
import { LabelModule } from '@vg-constellation/angular-19/label';
import { InternalCalloutComponent } from "@vg-constellation/angular-19/internal-callout";
import { ClientOnlyActionComponent } from '@vg-constellation/angular-19/client-only-action';
import { RedirectService } from './services/redirect/redirect.service';
import { HintErrorModule } from '@vg-constellation/angular-19/hint-error';
import { FormsModule } from '@angular/forms';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { AddAddressEffects } from '@app/effects/add-address.effect';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AddAddressHomeModule } from '@app/components/add-address-home/add-address-home.module';
import { CookieService } from 'ngx-cookie-service';
import { LoginErrorComponent } from './components/login-error/login-error.component';
import { ThirdPartyDocumentsService } from './services/thirdparty-documents/thirdparty-documents.service';
import { TPEOktaFlags } from './models/thirdparty-documents.model';

@NgModule({
    declarations: [AppComponent, ThirdpartyDocumentsComponent, LoginErrorComponent, OptimizeYourAccountComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [BrowserModule,
        AppRoutingModule,
        LinkModule,
        IconModule,
        NavOverflowModule,
        SpinnerModule,
        CardComponent,
        NgOptimizedImage,
        ButtonModule,
        StepIndicatorModule,
        NavOverflowModule,
        LabelModule,
        HintErrorModule,
        FormsModule,
        AddAddressHomeModule,
        InternalCalloutComponent,
        ClientOnlyActionComponent,
        StoreModule.forRoot(),
        StoreDevtoolsModule.instrument({ name: 'ThirdPartyDocuments', maxAge: 25 }),

        BannerComponent,
        EffectsModule.forRoot([AddAddressEffects])],   providers: [
    WindowRef,
    CaasService,
    RedirectService,
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (caasService: CaasService) => {
        return () => {
          return caasService.getContent();
        };
      },
      deps: [CaasService],
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory(http: HttpClient, meta: Meta, windowRef: WindowRef, cookieService: CookieService, redirectService: RedirectService) {
        const $window: Window = windowRef.nativeWindow;
        return async () => {
          const currentUrl = new URL(window.location.href);
          if (currentUrl.href.includes('login-error')) {
            $window.document.body.hidden = false;
            return;
          }

          if (environment.internal && !cookieService.get('POID')) {
            redirectService.redirectTo('/login-error?reason=poid-missing');
            return;
          }

          await firstValueFrom(http
            .get(`${environment.THIRDPARTY_DOCUMENTS.BASE_URL}/adobe`, { withCredentials: true }))
            .then((data: any) => {
              const spoid = data.sPOID;
              meta.updateTag({ name: 'DCSext.poid', content: data.poid });
              meta.updateTag({ name: 'DCSext.sPoid', content: spoid });
              meta.updateTag({ name: 'WT.seg_6', content: data.crewFlag });
              $window.document.body.hidden = false;
            })
            .catch(() => {
              const currentUrl = new URL(window.location.href);
              const goToInternal = currentUrl.searchParams.get('secureMessage');
              if (cookieService.get('POID') && goToInternal) {
                redirectService.redirectTo(environment.THIRDPARTY_DOCUMENTS.INTERNAL_URL);
              }
              else {
                redirectService.redirectTo(environment.LOGON_URL);
              }
              return Promise.reject();
            });
        };
      },
      deps: [HttpClient, Meta, WindowRef, CookieService, RedirectService],
    },

    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (thirdPartyService: ThirdPartyDocumentsService) => {
        return async () => {
          const currentUrl = new URL(window.location.href);
          if (currentUrl.href.includes('login-error')) {
            return Promise.resolve();
          }
          if (environment.internal) {
            const oktaFlag: TPEOktaFlags = await firstValueFrom(thirdPartyService.getOktaGroup());
            sessionStorage.setItem('oktaFlag', String(oktaFlag.duplicateAddressAddAndUpdate));
            return Promise.resolve();
          }
        };
      },
      deps: [ThirdPartyDocumentsService]
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory(gatekeeperService: GatekeeperService, windowRef: WindowRef) {
        const $window: Window = windowRef.nativeWindow;
        return (): Promise<any> => {
          const currentUrl = new URL(window.location.href);
          if (currentUrl.href.includes('login-error')) {
            return Promise.resolve();
          }

          return new Promise(async (resolve) => {
            await firstValueFrom(gatekeeperService.isFeatureSwitchedOn(environment.GATEKEEPER_FEATURE_ID))
              .then((response: boolean) => {
                if (response) {
                  const legacyUrl = new URL(environment.LEGACY_URL);
                  if (environment.internal) {
                    legacyUrl.port = window.location.port;
                  }
                  legacyUrl.pathname = environment.LEGACY_URL;
                  legacyUrl.searchParams.append('redirect', 'true');
                  $window.document.location.href = legacyUrl.toString();
                }
                else {
                  gatekeeperService.redirectToModernizedApp();
                }
                resolve(true);
              }).catch(() => {
                resolve(true);
              });
          });
        };
      },
      deps: [GatekeeperService, WindowRef],
    },
    {
      provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true
    },
    provideHttpClient(withInterceptorsFromDi()),
  ]
})
export class AppModule {
}
